// BM-015: Change Item bottom sheet.
//
// Lets the engineer change a single cart line's supplier, branch,
// fulfillment method, and/or quantity without leaving the cart page.
// Alternatives are sourced from api.getMasterProduct(masterProductId)
// — the same endpoint ProductDetail already uses — so the sheet gets
// realistic prices + branch stock without a new API surface.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { Check, MapPin, X } from 'lucide-react'
import clsx from 'clsx'
import { Button, QuantitySelector } from '../../radix'
import { api } from '../../../services/api'
import { formatBackorderAvailability, formatEarliestDate } from '../../../utils/datetime'
import { perUnitPrice as perUnit } from '../../../utils/pricing'
import { byDistanceNearestFirst, nearestBranchOptions } from '../../../utils/branchOptions'
import type { CartItem, SupplierListing } from '../../../types'

// Hotfix flag — client asked to hide the "Change supplier" affordance
// until GTIN-based matching lands (otherwise cross-supplier swaps can
// hop between wildly different products, e.g. copper fitting → concrete
// slab, because the master's alternate matches include bad matches).
// Everything else in the sheet (change branch, change quantity, change
// fulfilment method) stays live. Flip to true to re-enable.
const CHANGE_SUPPLIER_ENABLED = false

export interface ChangeItemApplyResult {
  // Original line (used by the parent to decide remove+add vs update).
  originalItemId: string
  originalSupplierId: string | number
  originalBranchId: string | number | null
  originalProductId: string | number
  // Target state.
  newProductId: string | number
  newSupplierId: string | number
  newBranchId: string
  newQuantity: number
  newMethod: 'pickup' | 'delivery'
  newPrice: number
  // Synthetic stock snapshot for the new (product, branch) at newQuantity,
  // sourced from the sheet's live-stock query + qty-aware DC recheck. The
  // parent seeds this into its lineStockOverrides map so the cart header
  // (delivery source branch + dates) is correct the instant the sheet
  // closes — no flicker to the pickup branch while cart-stock refetches.
  // Uses the same snake_case field names as the cart-stock endpoint so it
  // slots straight into the merge path.
  newStockSeed: {
    raw_product_id: string | number
    branch_id: string
    stock_quantity: number
    branch_stock: number
    // cap_total is the DC-aware max Cart uses to derive maxStock. Without
    // this the seed would set maxStock = branch shelf only, causing a
    // legit qty backed by DC to look over-stock — which triggers the
    // stock recheck effect and nukes the seed, recreating the flicker.
    cap_total: number
    collection_stock: number
    delivery_stock: number
    pickup_dc_stock: number
    delivery_dc_stock: number
    dc_stock: number | null
    can_collect: boolean
    can_deliver: boolean
    earliest_collection: string | null
    earliest_delivery: string | null
    pickup_dc_date: string | null
    delivery_dc_date: string | null
    // Backorder tier — absent on older responses = off.
    backorder_allowed?: boolean
    backorder_collection_date?: string | null
    backorder_delivery_date?: string | null
    backorder_date_from_api?: boolean
    delivery_source_branch_name: string | null
    delivery_source_branch_id: string | number | null
    delivery_source_branch_distance_miles: number | null
  }
}

export interface ChangeItemSheetProps {
  open: boolean
  item: CartItem
  currentMethod: 'pickup' | 'delivery'
  // Location for the live-stock probe. Passed from the cart's session
  // so the branch list matches what ProductDetail shows.
  postcode?: string
  latitude?: number
  longitude?: number
  // Optional over-cap seed — the qty currently visible on the cart card
  // that hasn't been synced to the cache (an over-stock entry sits in
  // Cart's pendingQuantities without touching the query cache). Without
  // this the sheet would open at item.quantity — the last committed
  // value — and the engineer would see 3 in the stepper after typing 5
  // on the card. Falls back to item.quantity when unset. The sheet's
  // Apply guard still blocks over-stock submits.
  initialQuantity?: number
  onClose: () => void
  onApply: (change: ChangeItemApplyResult) => Promise<void> | void
}

interface SupplierOption {
  supplierId: string | number
  supplierName: string
  unitPrice: number
  // Nearest branch of this supplier that has stock — used for the
  // supplier-picker row summary + as the default when supplier changes
  // (AC4 auto-select-nearest).
  nearestBranchId: string
  nearestBranchName: string
  nearestBranchDistanceMiles: number | null
  // Badges — computed by comparing across suppliers.
  badges: Array<'Best price' | 'Closest'>
}

interface BranchOption {
  branchId: string
  branchName: string
  distanceMiles: number | null
  earliestCollection: string | null
  earliestDelivery: string | null
  canCollect: boolean
  canDeliver: boolean
  // Live stock per method — feeds the quantity stepper's cap so the
  // engineer can't request more than the branch has for the chosen
  // fulfillment. Same source as canCollect/canDeliver derivation.
  // Kept as MAX(branch, DC) — do NOT change; other code (stepper cap,
  // canCollect gate) relies on this semantics.
  collectionStock: number
  deliveryStock: number
  // Split by tier so the branch-picker row can render a two-part
  // summary like "44 Today · 110 Tue 8 Sept" — the same shape
  // ProductDetail's pickup tile shows under the fulfilment title.
  // "No stock" is displayed only when BOTH tiers are 0.
  branchCollectionStock: number
  pickupDcStock: number
  pickupDcDate: string | null
  branchDeliveryStock: number
  // Delivery DC is tracked SEPARATELY from pickup DC because CPS
  // calculates them differently (pickup pre-subtracted; delivery raw).
  // Reusing pickupDcStock as a proxy hides delivery LATER tier when
  // pickup DC is 0 but delivery DC is positive.
  deliveryDcStock: number
  deliveryDcDate: string | null
  // Backorder tier: when allowed, a qty above the channel's stock (or zero
  // stock) is still orderable and shows the single backorder date.
  backorderAllowed: boolean
  backorderCollectionDate: string | null
  backorderDeliveryDate: string | null
  backorderDateFromApi: boolean
  // The branch that will actually fulfil delivery. Set by CPS/Wolseley
  // when delivery is borrowed from another branch; null when delivery
  // comes from this row's own branch. Delivery is system-determined
  // (BM-014 AC5) so this is display-only — matches ManageOrder's
  // "from {branchName}" annotation.
  deliverySourceBranchName: string | null
  deliverySourceBranchId: string | number | null
  deliverySourceBranchDistanceMiles: number | null
  // The specific listing id (raw productId) at this branch — needed for
  // the add-to-cart call after a supplier/branch swap.
  productId: string | number
}

// Live-price payload per raw product — same shape ProductDetail feeds
// its supplier grid. CEF (and any future supplier that flexes pack
// sizes) returns a fresh quantity_multiple alongside its live price;
// we carry BOTH so the unit-price division stays consistent between
// the "Best price" ranking, the per-unit display, and the multiple the
// stepper enforces. Dropping quantity_multiple was the earlier partial
// port — it made the sheet rank on stale pack sizes.
interface LivePriceOverride {
  price: number
  quantityMultiple?: number
}

// Per-supplier stock summary — the fields the sheet needs to mirror
// ProductDetail's Best-price / Closest badge logic AND its supplier
// picker row without pulling in PD's full listing shape.
//
// nearestStocked* — used by the Closest badge (excludes zero-stock).
// nearestBranch* — used by the supplier picker row (nearest branch
// regardless of stock, mirrors PD's supplier card which shows the
// nearest branch even when it has no stock, with a "Try another
// branch" hint).
interface SupplierStockSummary {
  hasAnyStock: boolean
  nearestStockedDistanceMiles: number | null
  nearestStockedBranchId: string | null
  nearestStockedBranchName: string | null
  nearestBranchDistanceMiles: number | null
  nearestBranchId: string | null
  nearestBranchName: string | null
}

// A single supplier listing (from getMasterProduct) has one branchId,
// one productId, one price. To rank suppliers we boil the many
// per-branch listings for a supplier down to a single row per supplier
// (cheapest + nearest info) so the picker can show price + a nearby-
// branch hint. Branch-level stock is checked with getLiveStock when
// the sheet builds branch options.
//
// livePriceByRawId — same live-price feed ProductDetail uses.
// stockBySupplierId — per-supplier live stock summary so badges match
// PD exactly: Best price excludes suppliers with no stock anywhere,
// and Closest is the nearest STOCKED branch (not the master's primary
// listing distance, which may be a random distant branch).
function buildSupplierOptions(
  listings: SupplierListing[],
  livePriceByRawId: Map<string, LivePriceOverride>,
  stockBySupplierId: Map<string, SupplierStockSummary>,
  // Hotfix — the source cart line's raw product id. When a supplier has
  // more than one active match on this master, we represent that
  // supplier with the SOURCE listing (falling back to cheapest if the
  // source belongs to a different supplier). Keeps the header price /
  // badges consistent with rawProductIdsBySupplier + the branch list,
  // both of which are also scoped to the source SKU.
  sourceProductId: string | number | null,
): SupplierOption[] {
  const priceFor = (listing: SupplierListing): number => {
    const override = livePriceByRawId.get(String(listing.productId))
    return override?.price != null ? override.price : listing.price
  }
  const multipleFor = (listing: SupplierListing): number | undefined => {
    const override = livePriceByRawId.get(String(listing.productId))
    return override?.quantityMultiple ?? listing.quantityMultiple
  }
  const bySupplier = new Map<string, SupplierListing[]>()
  for (const listing of listings) {
    if (listing.branchId == null) continue
    const key = String(listing.supplierId)
    const bucket = bySupplier.get(key) ?? []
    bucket.push(listing)
    bySupplier.set(key, bucket)
  }
  const sourceProductIdKey = sourceProductId != null ? String(sourceProductId) : null
  const options: SupplierOption[] = []
  for (const bucket of bySupplier.values()) {
    // Source SKU wins over cheapest when it's in this bucket. Prevents
    // a wrong-match SKU (e.g. £1.61 "DO NOT USE" placeholder) beating
    // the real cart-line SKU (£30 real product) and mis-labelling the
    // header price / "Best price" badge.
    const sourceListing = sourceProductIdKey != null
      ? bucket.find((l) => String(l.productId) === sourceProductIdKey)
      : undefined
    const representative = sourceListing ?? bucket.reduce(
      (best, l) => (priceFor(l) < priceFor(best) ? l : best),
      bucket[0],
    )
    const supplierIdKey = String(representative.supplierId)
    const stockSummary = stockBySupplierId.get(supplierIdKey)
    // Prefer the live-stock nearest branch (matches PD). Uses the
    // nearest branch REGARDLESS of stock so a no-stock supplier still
    // shows its closest branch + distance in the picker row (UKPS
    // reads "Southampton HPS · 4.5 mi", not the master snapshot's
    // distant primary branch without a distance). Falls back to the
    // master snapshot only while stock is still loading.
    const nearestFromStock = stockSummary?.nearestBranchId != null
      ? {
          branchId: stockSummary.nearestBranchId,
          branchName: stockSummary.nearestBranchName ?? '',
          distanceMiles: stockSummary.nearestBranchDistanceMiles,
        }
      : null
    const nearestFromSnapshot = bucket.reduce((best, l) => {
      if (l.distanceMiles == null) return best
      if (best.distanceMiles == null) return l
      return l.distanceMiles < best.distanceMiles ? l : best
    }, bucket[0])
    const nearest = nearestFromStock ?? {
      branchId: String(nearestFromSnapshot.branchId ?? ''),
      branchName: nearestFromSnapshot.branchName,
      distanceMiles: nearestFromSnapshot.distanceMiles,
    }
    options.push({
      supplierId: representative.supplierId,
      supplierName: representative.supplierName,
      unitPrice: perUnit(priceFor(representative), multipleFor(representative)),
      nearestBranchId: nearest.branchId,
      nearestBranchName: nearest.branchName,
      nearestBranchDistanceMiles: nearest.distanceMiles,
      badges: [],
    })
  }
  if (options.length === 0) return options

  // Skip both badges when only one supplier is available — mirrors PD's
  // hasMultipleListings gate (ProductDetail.tsx:939). With nothing to
  // compare against, a solo supplier shouldn't wear Best-price/Closest.
  if (options.length <= 1) return options

  // Best price — mirrors PD's supplierEligibility filter: only among
  // suppliers with SOME stock at SOME branch. UKPS with no stock
  // anywhere is excluded so a cheaper-but-unavailable supplier can't
  // win the badge. Suppliers whose stock query hasn't landed yet
  // (stockSummary undefined) are treated as eligible optimistically —
  // downgrading to no-badge would flicker.
  const eligibleOptions = options.filter((opt) => {
    const summary = stockBySupplierId.get(String(opt.supplierId))
    return summary == null ? true : summary.hasAnyStock
  })
  // Also mirror PD's !allPricesIdentical guard: when every eligible
  // supplier is the same price, "Best price" is meaningless.
  const allPricesIdentical =
    eligibleOptions.length > 1 &&
    eligibleOptions.every((o) => o.unitPrice === eligibleOptions[0].unitPrice)
  if (eligibleOptions.length > 0 && !allPricesIdentical) {
    const cheapest = eligibleOptions.reduce(
      (best, o) => (o.unitPrice < best.unitPrice ? o : best),
      eligibleOptions[0],
    )
    for (const opt of options) {
      if (opt.supplierId === cheapest.supplierId) opt.badges.push('Best price')
    }
  }

  // Closest — nearest STOCKED branch across suppliers (matches PD's
  // closestDistance computation via nearestStockedBranch). Suppliers
  // with no stocked branch don't contribute.
  const stockedDistances = options
    .map((opt) => {
      const summary = stockBySupplierId.get(String(opt.supplierId))
      return summary?.nearestStockedDistanceMiles
    })
    .filter((distanceMiles): distanceMiles is number => distanceMiles != null)
  if (stockedDistances.length > 0) {
    const closestDistance = Math.min(...stockedDistances)
    for (const opt of options) {
      const summary = stockBySupplierId.get(String(opt.supplierId))
      if (summary?.nearestStockedDistanceMiles === closestDistance) {
        opt.badges.push('Closest')
      }
    }
  }

  return options.sort((a, b) => a.unitPrice - b.unitPrice)
}

// Build branch options from a live-stock response scoped to the
// selected supplier. Same shape ProductDetail's branch picker consumes:
// per-branch collection_stock / delivery_stock / earliest_collection /
// earliest_delivery / can_collect / can_deliver.
interface LiveStockEntry {
  raw_product_id: string | number
  branch_id: string
  branch_name?: string
  distance_miles?: number | null
  stock_quantity?: number
  branch_stock?: number
  collection_stock?: number
  delivery_stock?: number
  // Depot / DC-backed stock. A branch can support pickup or delivery
  // via the supplier's warehouse even when its own shelf stock is 0
  // (Wolseley Order-on-Demand). ProductDetail treats these as available
  // via Math.max(branch_stock, dc_stock); the sheet must do the same
  // or Eastleigh's delivery-from-DC case reads as "Not available".
  pickup_dc_stock?: number | null
  delivery_dc_stock?: number | null
  dc_stock?: number | null
  pickup_dc_date?: string | null
  delivery_dc_date?: string | null
  can_collect?: boolean
  can_deliver?: boolean
  earliest_collection?: string | null
  earliest_delivery?: string | null
  // When CPS/Wolseley borrows delivery from another branch (this row's
  // own branch has no delivery capacity), the backend view resolves the
  // source branch code to a name and stamps it here. Null when the
  // delivery comes from this row's own branch. Same field ManageOrder
  // reads via item.deliverySourceBranchName.
  delivery_source_branch_name?: string | null
  delivery_source_branch_id?: string | number | null
  delivery_source_branch_distance_miles?: number | null
  // Backorder tier — absent on older responses = off.
  backorder_allowed?: boolean
  backorder_collection_date?: string | null
  backorder_delivery_date?: string | null
  backorder_date_from_api?: boolean
}

// The live-stock endpoint returns one entry per (raw_product_id, branch)
// — each of the supplier's branches has its OWN raw product id. To
// cover every branch we must pass every raw product id the supplier
// has on this master AND scope the call with supplierId (matching
// ProductDetail's per-supplier stock call). Filtering by a single raw
// product id — as an earlier version did — restricts the branch list
// to the primary listing's branch and hides every other branch that
// actually carries stock.
function branchOptionsFromLiveStock(
  stock: LiveStockEntry[],
  supplierRawProductIds: (string | number)[],
): BranchOption[] {
  if (supplierRawProductIds.length === 0) return []
  const supplierRawProductIdSet = new Set(supplierRawProductIds.map((rawId) => String(rawId)))
  return stock
    .filter((entry) => supplierRawProductIdSet.has(String(entry.raw_product_id)))
    .filter((entry) => entry.branch_id != null)
    .map((entry) => {
      const canCollectFlag = entry.can_collect !== false
      const canDeliverFlag = entry.can_deliver !== false
      const branchCollection = entry.collection_stock ?? entry.stock_quantity ?? entry.branch_stock ?? 0
      const branchDelivery = entry.delivery_stock ?? 0
      // DC-backed stock fallback — same rule ProductDetail applies
      // when it computes pickupMaxQty / deliveryMaxQty (see
      // ProductDetail.tsx canDeliverSelected / pickupMaxQty). Without
      // this, Wolseley Eastleigh (branch delivery 0, DC delivery > 0)
      // reads as "Not available" even though PD shows the delivery
      // date because the DC covers the order.
      const pickupDcStock = entry.pickup_dc_stock ?? entry.dc_stock ?? 0
      const deliveryDcStock = entry.delivery_dc_stock ?? entry.dc_stock ?? 0
      const collectionStock = Math.max(branchCollection, pickupDcStock ?? 0)
      const deliveryStock = Math.max(branchDelivery, deliveryDcStock ?? 0)
      const backorderAllowed = entry.backorder_allowed === true
      return {
        branchId: String(entry.branch_id),
        branchName: entry.branch_name ?? '',
        distanceMiles: entry.distance_miles ?? null,
        earliestCollection: entry.earliest_collection ?? null,
        earliestDelivery: entry.earliest_delivery ?? null,
        // Zero stock is still orderable on backorder (service type permitting)
        // — only when the channel has an availability date.
        canCollect:
          canCollectFlag &&
          (collectionStock > 0 ||
            (backorderAllowed && entry.backorder_collection_date != null)),
        canDeliver:
          canDeliverFlag &&
          (deliveryStock > 0 ||
            (backorderAllowed && entry.backorder_delivery_date != null)),
        backorderAllowed,
        backorderCollectionDate: entry.backorder_collection_date ?? null,
        backorderDeliveryDate: entry.backorder_delivery_date ?? null,
        backorderDateFromApi: entry.backorder_date_from_api === true,
        collectionStock,
        deliveryStock,
        branchCollectionStock: branchCollection,
        pickupDcStock: pickupDcStock ?? 0,
        pickupDcDate: entry.pickup_dc_date ?? null,
        branchDeliveryStock: branchDelivery,
        deliveryDcStock: deliveryDcStock ?? 0,
        deliveryDcDate: entry.delivery_dc_date ?? null,
        deliverySourceBranchName: entry.delivery_source_branch_name ?? null,
        deliverySourceBranchId: entry.delivery_source_branch_id ?? null,
        deliverySourceBranchDistanceMiles: entry.delivery_source_branch_distance_miles ?? null,
        // Use the entry's own raw product id — the id the ADD endpoint
        // needs to place this specific (product, branch) combination.
        productId: entry.raw_product_id,
      }
    })
    // Keep zero-stock branches in the option list so the sheet can
    // always surface the nearest real branch (with "Not available"
    // messaging) instead of leaving selectedBranchId pointing at the
    // stale cart branch. Matches ProductDetail's supplier card which
    // shows the primary branch even when it has no stock.
    .sort((a, b) => {
      if (a.distanceMiles == null) return 1
      if (b.distanceMiles == null) return -1
      return a.distanceMiles - b.distanceMiles
    })
}

// A branch option built from the cart line itself — the stock the cart page
// already fetched for this exact product + branch — for when live stock has
// no row for the line's branch: raw-only lines, which have no alternatives
// to fetch, and (DEV-51046) a line whose branch is no longer among the
// supplier's nearest after the engineer moved.
function branchOptionFromCartLine(item: CartItem): BranchOption | null {
  const branchIdStr = item.branchId != null ? String(item.branchId) : ''
  if (!branchIdStr) return null
  const canCollect = item.canCollect !== false
  const canDeliver = item.canDeliver !== false
  const pickupOption = item.fulfillmentOptions?.find((opt) => opt.type === 'pickup')
  const deliveryOption = item.fulfillmentOptions?.find((opt) => opt.type === 'delivery')
  // Same depot rule as branchOptionsFromLiveStock: pickup the DC can cover
  // counts, so a DC-backed line isn't read as unavailable or over-stock.
  const pickupDcStock = item.pickupDcStock ?? item.dcStock ?? 0
  const collectionStock = Math.max(
    pickupOption?.stockQuantity ?? item.maxStock ?? 0,
    pickupDcStock,
  )
  const deliveryStock = Math.max(
    deliveryOption?.stockQuantity ?? 0,
    item.deliveryDcStock ?? 0,
    item.dcStock ?? 0,
  )
  return {
    branchId: branchIdStr,
    branchName: item.branchName ?? '',
    distanceMiles: item.distanceMiles ?? null,
    earliestCollection: item.pickupOrderDate ?? null,
    earliestDelivery: item.deliveryOrderDate ?? null,
    canCollect:
      canCollect &&
      (collectionStock > 0 ||
        (item.backorderAllowed === true && item.backorderCollectionDate != null)),
    canDeliver:
      canDeliver &&
      (deliveryStock > 0 ||
        (item.backorderAllowed === true && item.backorderDeliveryDate != null)),
    backorderAllowed: item.backorderAllowed === true,
    backorderCollectionDate: item.backorderCollectionDate ?? null,
    backorderDeliveryDate: item.backorderDeliveryDate ?? null,
    backorderDateFromApi: item.backorderDateFromApi === true,
    branchCollectionStock: pickupOption?.stockQuantity ?? 0,
    pickupDcStock,
    pickupDcDate: item.pickupDcDate ?? null,
    branchDeliveryStock: deliveryOption?.stockQuantity ?? 0,
    deliveryDcStock: Math.max(item.deliveryDcStock ?? 0, item.dcStock ?? 0),
    deliveryDcDate: null,
    deliverySourceBranchName: item.deliverySourceBranchName ?? null,
    deliverySourceBranchId: item.deliverySourceBranchId ?? null,
    deliverySourceBranchDistanceMiles: item.deliverySourceBranchDistanceMiles ?? null,
    collectionStock,
    deliveryStock,
    productId: item.productId,
  }
}

export function ChangeItemSheet({
  open,
  item,
  currentMethod,
  postcode,
  latitude,
  longitude,
  initialQuantity,
  onClose,
  onApply,
}: ChangeItemSheetProps) {
  const seedQuantity = initialQuantity ?? item.quantity
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | number>(item.supplierId)
  const [selectedBranchId, setSelectedBranchId] = useState<string>(
    item.branchId != null ? String(item.branchId) : '',
  )
  const [method, setMethod] = useState<'pickup' | 'delivery'>(currentMethod)
  const [quantity, setQuantity] = useState<number>(seedQuantity)
  const [showSuppliers, setShowSuppliers] = useState(false)
  const [showBranches, setShowBranches] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  // Was the current branch chosen by the engineer via the "Change"
  // branch list, or seeded from the cart line (which may be stale) /
  // auto-select? A manual choice must stick even for a zero-stock
  // branch; a seeded one defers to auto-select's "nearest in-stock"
  // pick so the sheet lands on a usable branch (matches ProductDetail,
  // where the primary card leads with the closest branch that has
  // stock).
  const [hasUserSelectedBranch, setHasUserSelectedBranch] = useState(false)

  // Resync on open (parent may reuse the sheet across items).
  useEffect(() => {
    if (!open) return
    setSelectedSupplierId(item.supplierId)
    setSelectedBranchId(item.branchId != null ? String(item.branchId) : '')
    setMethod(currentMethod)
    setQuantity(seedQuantity)
    setShowSuppliers(false)
    setShowBranches(false)
    setHasUserSelectedBranch(false)
  }, [open, item.supplierId, item.branchId, seedQuantity, currentMethod])

  // Switching supplier discards the previous manual branch choice —
  // the branches list is now a different supplier's set, so
  // auto-select should re-run for the new supplier.
  useEffect(() => {
    setHasUserSelectedBranch(false)
  }, [selectedSupplierId])

  const { data: master, isLoading } = useQuery({
    queryKey: [
      'change-item-master',
      String(item.masterProductId ?? item.productId),
      postcode ?? '',
    ],
    queryFn: () =>
      item.masterProductId
        ? api.getMasterProduct(String(item.masterProductId), postcode, latitude, longitude)
        : Promise.resolve(null),
    enabled: open && !!item.masterProductId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // Cart lines whose product wasn't matched to a master (raw-only)
  // have masterProductId = null. The sheet's supplier / branch pickers
  // depend on master.supplierListings, so for these lines we run in a
  // degraded mode: no alternatives, but quantity + method can still be
  // changed against the current supplier/branch. Without this the
  // whole sheet would be unusable for raw-only lines.
  const hasMaster = !!item.masterProductId

  // Live prices for every supplier listing on this master — same source
  // ProductDetail uses so both screens agree on the "Best price" winner
  // and the unit price shown next to each supplier row. Falls back to
  // the master snapshot price when a listing is unavailable or the
  // live-price call hasn't landed yet.
  const rawProductIdsForPricing = useMemo<number[]>(
    () =>
      (master?.supplierListings ?? [])
        .map((listing) => Number(listing.productId))
        .filter((productId) => Number.isFinite(productId)),
    [master?.supplierListings],
  )

  const { data: livePricesData } = useQuery({
    queryKey: ['change-item-live-prices', rawProductIdsForPricing],
    queryFn: () => api.getLivePrices(rawProductIdsForPricing),
    enabled: open && rawProductIdsForPricing.length > 0,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const livePriceByRawId = useMemo<Map<string, LivePriceOverride>>(() => {
    const overrideMap = new Map<string, LivePriceOverride>()
    for (const priceEntry of livePricesData?.prices ?? []) {
      if (priceEntry?.unavailable || priceEntry?.soft_deleted) continue
      const parsedPrice = parseFloat(priceEntry.selling_price)
      if (!Number.isFinite(parsedPrice)) continue
      const parsedMultiple =
        priceEntry.quantity_multiple != null
          ? Number(priceEntry.quantity_multiple)
          : undefined
      overrideMap.set(String(priceEntry.raw_product_id), {
        price: parsedPrice,
        quantityMultiple:
          parsedMultiple != null && Number.isFinite(parsedMultiple) && parsedMultiple > 0
            ? parsedMultiple
            : undefined,
      })
    }
    return overrideMap
  }, [livePricesData?.prices])

  // ProductDetail-style per-supplier stock queries. Mirrors PD's
  // useQueries pattern exactly (see ProductDetail.tsx
  // rawProductIdsBySupplier + supplierStockQueries): one query per
  // supplier, each passing ALL that supplier's raw product ids + the
  // supplier id, so response entries expand to every nearby branch
  // regardless of which primary listing seeded them. Feeds both the
  // supplier picker's Best-price / Closest badges AND the selected
  // supplier's branch picker below — no redundant call.
  const rawProductIdsBySupplier = useMemo(() => {
    const bySupplier = new Map<string, (string | number)[]>()
    const cartLineProductIdKey = String(item.productId)
    for (const listing of master?.supplierListings ?? []) {
      if (listing.noNearbyBranch) continue
      if (listing.productId == null) continue
      const key = String(listing.supplierId)
      const isSourceListing = String(listing.productId) === cartLineProductIdKey
      // Hotfix — keep ONE raw product per supplier for the branch list.
      // A master with multiple active ProductMatch rows for the same
      // supplier makes the live-stock response return one row per
      // (raw_product_id, branch), which flattens into duplicate branches
      // in the Change Branch list (same branch listed N times, click
      // selects all N).
      //
      // The source cart line's own productId ALWAYS wins for its
      // supplier — swapping to another SKU on a qty-only edit would
      // silently mutate the cart line to a different (possibly wrong-
      // match) raw product on apply, because Cart's applyChangeItem
      // routes any newProductId != originalProductId through the
      // remove+add path (Cart.tsx:1220). For non-source suppliers, the
      // first-encountered listing is used (backend sorts by price, so
      // "first" = cheapest). Change-supplier is currently hidden so
      // non-source suppliers only feed badges/summary logic, not user
      // action — full per-listing model deferred to post-GTIN.
      if (isSourceListing) {
        bySupplier.set(key, [listing.productId])
        continue
      }
      if (bySupplier.has(key)) continue
      bySupplier.set(key, [listing.productId])
    }
    return bySupplier
  }, [master?.supplierListings, item.productId])

  const supplierIdKeysInOrder = useMemo(
    () =>
      Array.from(rawProductIdsBySupplier.keys()).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true }),
      ),
    [rawProductIdsBySupplier],
  )

  const supplierStockQueries = useQueries({
    queries: supplierIdKeysInOrder.map((supplierIdKey) => {
      const rawIds = rawProductIdsBySupplier.get(supplierIdKey) ?? []
      const supplierIdNumeric = Number(supplierIdKey)
      const supplierIdParam = Number.isFinite(supplierIdNumeric) ? supplierIdNumeric : undefined
      return {
        queryKey: [
          'change-item-supplier-stock',
          supplierIdKey,
          rawIds.map((rawId) => String(rawId)).join(','),
          postcode ?? '',
        ],
        queryFn: async (): Promise<{ stock: LiveStockEntry[] } | null> => {
          if (!postcode || rawIds.length === 0) return null
          try {
            return await api.getLiveStock(rawIds, postcode, latitude, longitude, supplierIdParam)
          } catch {
            return null
          }
        },
        enabled: open && !!postcode && rawIds.length > 0,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
      }
    }),
  })

  // Per-supplier stock summary: has any stocked branch + nearest
  // stocked branch info. Mirrors ProductDetail's nearestStockedBranch +
  // supplierEligibility so Best-price / Closest badges line up.
  const stockBySupplierId = useMemo<Map<string, SupplierStockSummary>>(() => {
    const summary = new Map<string, SupplierStockSummary>()
    supplierIdKeysInOrder.forEach((supplierIdKey, idx) => {
      const rawIds = rawProductIdsBySupplier.get(supplierIdKey) ?? []
      const supplierStock = supplierStockQueries[idx]?.data?.stock ?? null
      if (supplierStock == null) {
        // Query still loading or errored — summary undefined for this
        // supplier, buildSupplierOptions will treat it optimistically.
        return
      }
      const allBranches = branchOptionsFromLiveStock(supplierStock, rawIds)
      const stockedBranches = allBranches.filter(
        (option) => option.canCollect || option.canDeliver,
      )
      const nearestOfList = (branches: BranchOption[]): BranchOption | null =>
        branches.reduce<BranchOption | null>((best, option) => {
          if (option.distanceMiles == null) return best
          if (best == null || best.distanceMiles == null) return option
          return option.distanceMiles < best.distanceMiles ? option : best
        }, null)
      const nearestStocked = nearestOfList(stockedBranches)
      const nearestAny = nearestOfList(allBranches) ?? allBranches[0] ?? null
      summary.set(supplierIdKey, {
        hasAnyStock: stockedBranches.length > 0,
        nearestStockedDistanceMiles: nearestStocked?.distanceMiles ?? null,
        nearestStockedBranchId: nearestStocked?.branchId ?? null,
        nearestStockedBranchName: nearestStocked?.branchName ?? null,
        nearestBranchDistanceMiles: nearestAny?.distanceMiles ?? null,
        nearestBranchId: nearestAny?.branchId ?? null,
        nearestBranchName: nearestAny?.branchName ?? null,
      })
    })
    return summary
  }, [supplierIdKeysInOrder, rawProductIdsBySupplier, supplierStockQueries])

  const supplierOptions = useMemo(
    () =>
      buildSupplierOptions(
        master?.supplierListings ?? [],
        livePriceByRawId,
        stockBySupplierId,
        item.productId,
      ),
    [master?.supplierListings, livePriceByRawId, stockBySupplierId, item.productId],
  )

  // Branch picker for the SELECTED supplier reuses that supplier's
  // stock query — no separate fetch needed.
  const selectedSupplierRawProductIds = useMemo<(string | number)[]>(
    () => rawProductIdsBySupplier.get(String(selectedSupplierId)) ?? [],
    [rawProductIdsBySupplier, selectedSupplierId],
  )

  const selectedSupplierQueryIndex = supplierIdKeysInOrder.indexOf(String(selectedSupplierId))
  const selectedSupplierStockQuery =
    selectedSupplierQueryIndex >= 0 ? supplierStockQueries[selectedSupplierQueryIndex] : undefined
  const branchStock = selectedSupplierStockQuery?.data ?? null
  const isBranchStockLoading = selectedSupplierStockQuery?.isLoading ?? false
  const isBranchStockFetching = selectedSupplierStockQuery?.isFetching ?? false

  const branchOptions = useMemo(() => {
    // Raw-only lines don't have a master, so no live-stock alternatives.
    // Synthesise a single branch option from the cart line itself so
    // the render code (which reads from selectedBranchOption) still
    // works and the engineer can flip method / change qty in place.
    if (!hasMaster) {
      const lineOption = branchOptionFromCartLine(item)
      return lineOption ? [lineOption] : []
    }
    const liveOptions = branchOptionsFromLiveStock(
      branchStock?.stock ?? [],
      selectedSupplierRawProductIds,
    )
    // DEV-51046 (AC5): live stock only covers the supplier's nearest few
    // branches, so after a move the branch this line is at can drop out of
    // it. Keep it, from the line's own stock, so it stays selected and
    // listed rather than auto-select quietly moving the line.
    if (!branchStock?.stock || String(selectedSupplierId) !== String(item.supplierId)) {
      return liveOptions
    }
    const lineOption = branchOptionFromCartLine(item)
    if (!lineOption || liveOptions.some((b) => b.branchId === lineOption.branchId)) {
      return liveOptions
    }
    return [...liveOptions, lineOption].sort(byDistanceNearestFirst)
  }, [hasMaster, item, branchStock?.stock, selectedSupplierRawProductIds, selectedSupplierId])

  // Distinguish "loading" from "no branch available" so the Pickup /
  // Delivery cards can show a checking state instead of blindly
  // displaying "Not available" for the sub-second between opening the
  // sheet and the stock response landing.
  const isBranchStockPending =
    isBranchStockLoading || (isBranchStockFetching && !branchStock)

  // Qty-aware DC-date refresh, per branch. The picker's stock fetch
  // (`change-item-supplier-stock`) doesn't send a quantity, so the
  // pickupDcDate it returns is only correct for qty=1 — Wolseley/CPS
  // return different DC dates at different quantities. To avoid
  // showing misleading dates when the engineer's line qty exceeds a
  // branch's shelf stock, rerun api.checkStock per affected branch
  // with the actual qty and stash the fresh date here. SLA-based
  // suppliers (UKPS/CEF/YESSS) return the same date regardless of
  // qty — the extra call is a no-op waste for them, but keeps the
  // code branch-agnostic.
  type FreshDcDate = {
    qty: number
    pickupDate: string | null
    deliveryDate: string | null
    // CPS picks the delivery source branch as "any nearby branch with
    // stock" — that pick can differ between qty=1 and qty=100. Carry
    // the source through the recheck so the "from X" annotation on the
    // Delivery tile shows the branch that will actually fulfil the
    // engineer's current qty, not the qty=1 source.
    deliverySourceBranchName: string | null
    // Supplier's earliest dates at THIS qty — the backorder date for API-date
    // suppliers (Wolseley). Ignored for admin-date suppliers.
    backorderPickupDate?: string | null
    backorderDeliveryDate?: string | null
    isChecking: boolean
  }
  const [freshDcDates, setFreshDcDates] = useState<Map<string, FreshDcDate>>(new Map())
  // Per-branch sequence counter — discard responses that a later qty
  // change has already superseded, so an in-flight response can't
  // clobber a fresher result.
  const dcRecheckSeqRef = useRef<Map<string, number>>(new Map())

  // Wipe fresh dates when the underlying set of branches changes
  // (supplier swap, new master fetch). Stale entries for absent
  // branches never render but leaking them wastes memory.
  useEffect(() => {
    setFreshDcDates(new Map())
    dcRecheckSeqRef.current = new Map()
  }, [selectedSupplierId, master?.id])

  useEffect(() => {
    if (!open || !hasMaster || !postcode) return

    const supplierIdNumeric = Number(selectedSupplierId)
    if (!Number.isFinite(supplierIdNumeric)) return

    // Refetch for every branch where DC could contribute to either the
    // pickup or delivery display. Covers three cases: (a) DC row rendered
    // in the picker (pickupDcStock > 0), (b) qty exceeds branch pickup
    // stock (so the pickup tile date needs the DC fallback), (c) qty
    // exceeds branch delivery stock (delivery tile date needs the DC
    // fallback).
    const targets = branchOptions.filter(
      (branch) =>
        branch.pickupDcStock > 0 ||
        quantity > branch.branchCollectionStock ||
        quantity > branch.branchDeliveryStock,
    )
    if (targets.length === 0) return

    // Debounce so a stepper burst fires one call per branch per settled
    // qty, not one per keystroke.
    const timer = setTimeout(() => {
      targets.forEach((branch) => {
        const branchKey = String(branch.branchId)
        const existing = freshDcDates.get(branchKey)
        // Already have a settled result for this exact qty — skip.
        if (existing && existing.qty === quantity && !existing.isChecking) return

        // Find the supplier product code for this specific branch's
        // raw listing — checkStock needs the CODE, not the raw id.
        const listing = (master?.supplierListings ?? []).find(
          (candidate) => String(candidate.productId) === String(branch.productId),
        )
        const productCode = listing?.supplierProductCode
        if (!productCode) return

        const seq = (dcRecheckSeqRef.current.get(branchKey) ?? 0) + 1
        dcRecheckSeqRef.current.set(branchKey, seq)
        setFreshDcDates((prev) => {
          const next = new Map(prev)
          next.set(branchKey, {
            qty: quantity,
            pickupDate: null,
            deliveryDate: null,
            deliverySourceBranchName: null,
            isChecking: true,
          })
          return next
        })

        api
          .checkStock({
            supplier_id: supplierIdNumeric,
            product_codes: [productCode],
            branch_id: branchKey,
            postcode,
            quantity,
            bypass_cache: true,
          })
          .then((response: unknown) => {
            if (dcRecheckSeqRef.current.get(branchKey) !== seq) return
            const items = (response as {
              items?: Array<{
                pickup_dc_date?: string | null
                delivery_dc_date?: string | null
                delivery_source_branch_name?: string | null
                earliest_collection?: string | null
                earliest_delivery?: string | null
              }>
            })?.items
            const pickupDate = items?.[0]?.pickup_dc_date ?? null
            const deliveryDate = items?.[0]?.delivery_dc_date ?? null
            const deliverySourceBranchName =
              items?.[0]?.delivery_source_branch_name ?? null
            setFreshDcDates((prev) => {
              const next = new Map(prev)
              next.set(branchKey, {
                qty: quantity,
                pickupDate,
                deliveryDate,
                deliverySourceBranchName,
                backorderPickupDate: items?.[0]?.earliest_collection ?? null,
                backorderDeliveryDate: items?.[0]?.earliest_delivery ?? null,
                isChecking: false,
              })
              return next
            })
          })
          .catch(() => {
            if (dcRecheckSeqRef.current.get(branchKey) !== seq) return
            setFreshDcDates((prev) => {
              const next = new Map(prev)
              next.set(branchKey, {
                qty: quantity,
                pickupDate: null,
                deliveryDate: null,
                isChecking: false,
              })
              return next
            })
          })
      })
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasMaster, postcode, selectedSupplierId, quantity, branchOptions, master?.supplierListings])

  // Auto-select a branch when the sheet opens.
  //
  // Ordering:
  //   1. If the engineer has manually picked a branch from the list,
  //      honour it (even for zero-stock branches).
  //   2. Else if the current selection IS the cart-line seed and it is
  //      still in the eligible list, keep it — opening Change Item must
  //      show the branch the item is actually on today.
  //   3. Else (seed dropped off, or the selection came from a supplier
  //      switch — which uses supplier.nearestBranchId, nearest REGARDLESS
  //      of stock) fall back to nearest in-stock, else first. Without
  //      this, switching to a supplier whose nearest branch is out of
  //      stock leaves both Pickup and Delivery disabled and Update
  //      greyed out — the engineer would think the sheet was broken.
  const cartLineSupplierIdStr = String(item.supplierId)
  const cartLineBranchIdStr = item.branchId != null ? String(item.branchId) : ''
  const isSeedFromCartLine =
    String(selectedSupplierId) === cartLineSupplierIdStr &&
    selectedBranchId === cartLineBranchIdStr
  useEffect(() => {
    if (branchOptions.length === 0) return
    if (hasUserSelectedBranch) return
    if (isSeedFromCartLine) {
      const seedStillEligible = branchOptions.some((b) => b.branchId === selectedBranchId)
      if (seedStillEligible) return
    }
    const nearestInStock = branchOptions.find((b) => b.canCollect || b.canDeliver)
    const target = nearestInStock ?? branchOptions[0]
    if (target.branchId !== selectedBranchId) {
      setSelectedBranchId(target.branchId)
    }
  }, [branchOptions, hasUserSelectedBranch, selectedBranchId, isSeedFromCartLine])

  const selectedSupplierOption = supplierOptions.find(
    (o) => String(o.supplierId) === String(selectedSupplierId),
  )
  const selectedBranchOption = branchOptions.find((b) => b.branchId === selectedBranchId)

  // What the picker actually lists (AC5): the nearest few to where the
  // engineer is now, plus the branch the line is at when it isn't one of
  // them. `branchOptions` stays whole — auto-select and the stock/date
  // lookups above still reason over every branch the supplier returned.
  const visibleBranchOptions = useMemo(
    () => nearestBranchOptions(branchOptions, cartLineBranchIdStr),
    [branchOptions, cartLineBranchIdStr],
  )

  // Is the currently chosen method actually usable at the selected
  // branch? Feeds the Apply button so the engineer can't submit a
  // no-stock combo (previously guaranteed by filtering zero-stock
  // branches out, but we now keep them in the list so the picker can
  // still show real branch info).
  const methodAvailableAtBranch =
    method === 'pickup'
      ? selectedBranchOption?.canCollect === true
      : selectedBranchOption?.canDeliver === true

  // Same validation ProductDetail's footer applies (ProductDetail.tsx
  // isOverStock / isInvalidMultiple). The +/- controls already snap
  // to a valid multiple, but the shared QuantitySelector still lets
  // the engineer TYPE an off-multiple or over-stock value — both the
  // PATCH and replacement POST reject those. Surface the reason in
  // the Update button's label and gate the button.

  // If the selected branch can't do the chosen method, flip to the
  // supported one so the price + total stay accurate.
  useEffect(() => {
    if (!selectedBranchOption) return
    if (method === 'pickup' && !selectedBranchOption.canCollect && selectedBranchOption.canDeliver) {
      setMethod('delivery')
    } else if (method === 'delivery' && !selectedBranchOption.canDeliver && selectedBranchOption.canCollect) {
      setMethod('pickup')
    }
  }, [selectedBranchOption, method])

  const unitPrice = selectedSupplierOption?.unitPrice ?? perUnit(item.price, item.quantityMultiple)
  const lineTotal = unitPrice * quantity

  // Pack size for the SELECTED listing. Hotfix: with change-supplier
  // hidden, the selected listing IS the source cart line's raw product —
  // look up by productId so a bad-match SKU in the same supplier bucket
  // (that would otherwise win via first-in-listings order) can't swap
  // the pack size or live-price override under a qty-only edit. Falls
  // back to the first listing for the current supplier when the source
  // raw product isn't in the master (shouldn't happen for a live cart
  // line, but defensive so orderMultiple never lands as `1` when the
  // real value is 10). Both the PATCH and the replacement POST reject
  // off-multiple quantities.
  const selectedListing =
    (master?.supplierListings ?? []).find(
      (listing) => String(listing.productId) === String(item.productId),
    ) ??
    (master?.supplierListings ?? []).find(
      (listing) => String(listing.supplierId) === String(selectedSupplierId),
    )
  const selectedLiveOverride = selectedListing
    ? livePriceByRawId.get(String(selectedListing.productId))
    : undefined
  const orderMultiple =
    selectedLiveOverride?.quantityMultiple ??
    selectedListing?.quantityMultiple ??
    item.quantityMultiple ??
    1

  // Available stock for the current method — feeds the stepper's cap.
  const stockForMethod =
    selectedBranchOption
      ? method === 'pickup'
        ? selectedBranchOption.collectionStock
        : selectedBranchOption.deliveryStock
      : undefined

  // Same gates ProductDetail applies to its footer button.
  // isInvalidMultiple catches typed off-multiple quantities (e.g. 9
  // for a pack of 10) that the +/- controls would never produce.
  // isOverStock catches typed values above the branch's live stock.
  const isInvalidMultiple =
    orderMultiple > 1 && quantity > 0 && quantity % orderMultiple !== 0
  // Backorder: above-stock (or zero-stock) quantities are orderable when the
  // supplier/product allows it for this branch/channel AND the channel has an
  // availability date (qty-matched for API-date suppliers; while that probe
  // is in flight the Apply button already waits on isBranchStockPending /
  // the checking state).
  const selectedFreshDcEarly = selectedBranchOption
    ? freshDcDates.get(String(selectedBranchOption.branchId))
    : undefined
  const selectedFreshReadyEarly =
    selectedFreshDcEarly?.qty === quantity && !selectedFreshDcEarly.isChecking
  const useFreshBackorderDates =
    selectedBranchOption?.backorderDateFromApi === true && selectedFreshReadyEarly
  const isCheckingBackorderDate =
    selectedBranchOption?.backorderDateFromApi === true &&
    selectedFreshDcEarly?.qty === quantity &&
    selectedFreshDcEarly.isChecking
  const backorderCollectionDate = useFreshBackorderDates
    ? (selectedFreshDcEarly?.backorderPickupDate ?? null)
    : (selectedBranchOption?.backorderCollectionDate ?? null)
  const backorderDeliveryDate = useFreshBackorderDates
    ? (selectedFreshDcEarly?.backorderDeliveryDate ?? null)
    : (selectedBranchOption?.backorderDeliveryDate ?? null)
  const canBackorderForMethod =
    selectedBranchOption?.backorderAllowed === true &&
    (method === 'pickup'
      ? selectedBranchOption.canCollect && backorderCollectionDate != null
      : selectedBranchOption.canDeliver && backorderDeliveryDate != null)
  const isOverStock =
    !canBackorderForMethod &&
    stockForMethod != null && stockForMethod > 0 && quantity > stockForMethod

  // When the multiple changes (supplier swap), snap the quantity up to
  // the nearest valid multiple so the sheet never renders an invalid
  // starting value — QuantitySelector then keeps it on the grid.
  useEffect(() => {
    if (orderMultiple <= 1) return
    if (quantity % orderMultiple === 0) return
    setQuantity(Math.ceil(quantity / orderMultiple) * orderMultiple)
  }, [orderMultiple, quantity])

  const currentBranchIdStr = item.branchId != null ? String(item.branchId) : ''
  const currentProductIdStr = String(item.productId)
  const hasChanges =
    String(selectedSupplierId) !== String(item.supplierId) ||
    selectedBranchId !== currentBranchIdStr ||
    method !== currentMethod ||
    quantity !== item.quantity ||
    (selectedBranchOption != null && String(selectedBranchOption.productId) !== currentProductIdStr)

  const handleApply = async () => {
    if (!hasChanges || isApplying || !selectedBranchOption || !item.id) return
    setIsApplying(true)
    try {
      // Build the stock seed from the sheet's already-loaded live-stock
      // data. Prefer the qty-aware recheck when it settled at the current
      // quantity — otherwise fall back to the initial (qty=1) branch fetch.
      // Cart's applyChangeItem will drop this into lineStockOverrides so
      // the cart header renders the correct delivery source instantly.
      const freshDcForSelected = freshDcDates.get(String(selectedBranchOption.branchId))
      const freshDcReady =
        freshDcForSelected?.qty === quantity && !freshDcForSelected.isChecking
      await onApply({
        originalItemId: item.id,
        originalSupplierId: item.supplierId,
        originalBranchId: item.branchId,
        originalProductId: item.productId,
        newProductId: selectedBranchOption.productId,
        newSupplierId: selectedSupplierId,
        newBranchId: selectedBranchId,
        newQuantity: quantity,
        newMethod: method,
        newPrice: unitPrice,
        newStockSeed: {
          raw_product_id: selectedBranchOption.productId,
          branch_id: selectedBranchId,
          stock_quantity: selectedBranchOption.branchCollectionStock,
          branch_stock: selectedBranchOption.branchCollectionStock,
          // DC-aware cap so the seeded line's maxStock covers DC-backed
          // qtys — same MAX(branch, DC, delivery) rule the backend applies.
          // BranchOption.collectionStock/deliveryStock are already
          // MAX(branch, DC) per method; take the greater of the two.
          cap_total: Math.max(
            selectedBranchOption.collectionStock,
            selectedBranchOption.deliveryStock,
          ),
          collection_stock: selectedBranchOption.collectionStock,
          delivery_stock: selectedBranchOption.deliveryStock,
          pickup_dc_stock: selectedBranchOption.pickupDcStock,
          delivery_dc_stock: selectedBranchOption.deliveryDcStock,
          // Legacy combined DC field — take the greater of the two per-channel
          // values so hasDc gates upstream stay true when either channel
          // has depot backing.
          dc_stock: Math.max(
            selectedBranchOption.pickupDcStock,
            selectedBranchOption.deliveryDcStock,
          ) > 0
            ? Math.max(
                selectedBranchOption.pickupDcStock,
                selectedBranchOption.deliveryDcStock,
              )
            : null,
          can_collect: selectedBranchOption.canCollect,
          can_deliver: selectedBranchOption.canDeliver,
          earliest_collection: selectedBranchOption.earliestCollection,
          earliest_delivery: selectedBranchOption.earliestDelivery,
          backorder_allowed: selectedBranchOption.backorderAllowed,
          // Qty-matched for API-date suppliers (see backorderCollectionDate).
          backorder_collection_date: backorderCollectionDate,
          backorder_delivery_date: backorderDeliveryDate,
          backorder_date_from_api: selectedBranchOption.backorderDateFromApi,
          pickup_dc_date:
            (freshDcReady ? freshDcForSelected?.pickupDate : null) ??
            selectedBranchOption.pickupDcDate,
          delivery_dc_date:
            (freshDcReady ? freshDcForSelected?.deliveryDate : null) ??
            selectedBranchOption.deliveryDcDate,
          delivery_source_branch_name:
            (freshDcReady ? freshDcForSelected?.deliverySourceBranchName : null) ??
            selectedBranchOption.deliverySourceBranchName,
          delivery_source_branch_id: selectedBranchOption.deliverySourceBranchId,
          delivery_source_branch_distance_miles:
            selectedBranchOption.deliverySourceBranchDistanceMiles,
        },
      })
      onClose()
    } finally {
      setIsApplying(false)
    }
  }

  // Qty-aware date resolution for the tile headers. When the engineer's
  // quantity fits within the branch's own shelf stock, the branch date is
  // accurate and used. When qty exceeds branch shelf, the order really
  // ships from the DC — prefer the fresh qty-matched date from
  // freshDcDates when settled, fall back to the initial qty=1 DC date,
  // then finally the branch date as a last resort.
  const selectedFreshDc = selectedBranchOption
    ? freshDcDates.get(String(selectedBranchOption.branchId))
    : undefined
  const selectedFreshReady =
    selectedFreshDc?.qty === quantity && !selectedFreshDc.isChecking
  const branchCoversPickup =
    !!selectedBranchOption && quantity <= selectedBranchOption.branchCollectionStock
  const branchCoversDelivery =
    !!selectedBranchOption && quantity <= selectedBranchOption.branchDeliveryStock
  const effectivePickupDate = branchCoversPickup
    ? selectedBranchOption?.earliestCollection ?? null
    : (selectedFreshReady ? selectedFreshDc?.pickupDate : null) ??
      selectedBranchOption?.pickupDcDate ??
      selectedBranchOption?.earliestCollection ??
      null
  const effectiveDeliveryDate = branchCoversDelivery
    ? selectedBranchOption?.earliestDelivery ?? null
    : (selectedFreshReady ? selectedFreshDc?.deliveryDate : null) ??
      selectedBranchOption?.deliveryDcDate ??
      selectedBranchOption?.earliestDelivery ??
      null
  // Backorder per channel: qty above everything the channel can supply
  // (branch, depot, borrowed branch) → the single backorder date.
  const isPickupBackorder =
    selectedBranchOption?.backorderAllowed === true &&
    selectedBranchOption.canCollect &&
    quantity > selectedBranchOption.collectionStock &&
    (backorderCollectionDate != null || isCheckingBackorderDate)
  const isDeliveryBackorder =
    selectedBranchOption?.backorderAllowed === true &&
    selectedBranchOption.canDeliver &&
    quantity > selectedBranchOption.deliveryStock &&
    (backorderDeliveryDate != null || isCheckingBackorderDate)
  const backorderPickupLabel = (() => {
    if (isCheckingBackorderDate) return 'Checking availability…'
    return (
      formatBackorderAvailability(backorderCollectionDate) ??
      'Collection date to be confirmed'
    )
  })()
  const backorderDeliveryLabel = (() => {
    if (isCheckingBackorderDate) return 'Checking availability…'
    return (
      formatBackorderAvailability(backorderDeliveryDate) ??
      'Delivery date to be confirmed'
    )
  })()
  const collectionDateLabel = isPickupBackorder
    ? backorderPickupLabel
    : formatEarliestDate(effectivePickupDate)
  const deliveryDateLabel = isDeliveryBackorder
    ? backorderDeliveryLabel
    : formatEarliestDate(effectiveDeliveryDate)

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && !isApplying && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <Dialog.Content
          className="fixed bottom-0 left-1/2 -translate-x-1/2 z-50 w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl shadow-xl focus:outline-none"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="h-1 w-9 rounded-full bg-gray-300" />
          </div>

          {/* Header */}
          <div className="px-4 py-3 flex items-start justify-between gap-3">
            <div>
              <Dialog.Title className="text-base font-bold text-gray-900">
                Change item
              </Dialog.Title>
              <Dialog.Description className="text-xs text-gray-500 mt-0.5">
                Only this item changes
              </Dialog.Description>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-none w-8 h-8 grid place-items-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-4 pb-4 space-y-4">
            {/* Product + Supplier + Quantity — grouped as item details */}
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
              {/* Product */}
              <div className="px-4 py-3 bg-gray-50">
                <p className="text-sm font-bold text-gray-900">{item.productName}</p>
                {item.supplierProductCode && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {item.supplierProductCode}
                    {item.supplierName ? ` · ${item.supplierName}` : ''}
                  </p>
                )}
              </div>

              {/* Supplier (AC2) — hidden while CHANGE_SUPPLIER_ENABLED is false */}
              {CHANGE_SUPPLIER_ENABLED && (
              <div className="px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-400">Supplier</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <p className="text-sm text-gray-900 truncate">
                        {selectedSupplierOption?.supplierName ?? item.supplierName}
                      </p>
                      {selectedSupplierOption && (
                        <span className="text-xs text-gray-400">
                          {selectedSupplierOption.nearestBranchName}
                          {selectedSupplierOption.nearestBranchDistanceMiles != null &&
                            ` · ${selectedSupplierOption.nearestBranchDistanceMiles.toFixed(1)} mi`}
                        </span>
                      )}
                      {selectedSupplierOption?.badges.map((badge) => (
                        <span
                          key={badge}
                          className={clsx(
                            'text-[10px] font-semibold px-1.5 py-0.5 rounded',
                            badge === 'Best price'
                              ? 'text-emerald-700 bg-emerald-50'
                              : 'text-blue-700 bg-blue-50',
                          )}
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>
                  {CHANGE_SUPPLIER_ENABLED && supplierOptions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setShowSuppliers((s) => !s)}
                      className="text-xs font-semibold text-brand-primary hover:opacity-80 transition-opacity flex-shrink-0"
                    >
                      Change
                    </button>
                  )}
                </div>

                {CHANGE_SUPPLIER_ENABLED && showSuppliers && (
                  <div className="mt-3 rounded-lg border border-gray-200 overflow-hidden">
                    {supplierOptions.map((supplier, idx) => {
                      const isSelected =
                        String(supplier.supplierId) === String(selectedSupplierId)
                      return (
                        <button
                          key={String(supplier.supplierId)}
                          type="button"
                          onClick={() => {
                            setSelectedSupplierId(supplier.supplierId)
                            // AC4 — auto-select nearest branch of the new supplier.
                            setSelectedBranchId(supplier.nearestBranchId)
                            setShowSuppliers(false)
                            setShowBranches(false)
                          }}
                          className={clsx(
                            'w-full px-3 py-2.5 text-left flex items-center justify-between transition-colors hover:bg-gray-50',
                            idx > 0 && 'border-t border-gray-100',
                            isSelected && 'bg-gray-50',
                          )}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm text-gray-900">{supplier.supplierName}</p>
                              <span className="text-xs text-gray-400">
                                {supplier.nearestBranchName}
                                {supplier.nearestBranchDistanceMiles != null &&
                                  ` · ${supplier.nearestBranchDistanceMiles.toFixed(1)} mi`}
                              </span>
                              {supplier.badges.map((badge) => (
                                <span
                                  key={badge}
                                  className={clsx(
                                    'text-[10px] font-semibold px-1.5 py-0.5 rounded',
                                    badge === 'Best price'
                                      ? 'text-emerald-700 bg-emerald-50'
                                      : 'text-blue-700 bg-blue-50',
                                  )}
                                >
                                  {badge}
                                </span>
                              ))}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">
                              £{supplier.unitPrice.toFixed(2)} / each
                            </p>
                          </div>
                          {isSelected && (
                            <Check className="w-4 h-4 text-brand-primary flex-shrink-0" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
              )}

              {/* Quantity (AC5) + line total. Uses the shared
                  QuantitySelector so pack products step by their
                  quantityMultiple and can't be nudged into an invalid
                  quantity (which both the PATCH and the replacement
                  POST reject). Live-stock cap for the selected
                  method mirrors the cart's per-line cap. */}
              <div className="px-4 py-3 flex items-center justify-between bg-gray-50">
                <div>
                  <p className="text-xs text-gray-400">Price</p>
                  <p className="text-sm font-bold text-gray-900 mt-0.5">
                    £{lineTotal.toFixed(2)}{' '}
                    <span className="font-normal text-gray-400">
                      · £{unitPrice.toFixed(2)} each
                    </span>
                  </p>
                </div>
                <QuantitySelector
                  size="sm"
                  value={quantity}
                  onChange={(next) => setQuantity(next ?? orderMultiple)}
                  multiple={orderMultiple}
                  stock={stockForMethod}
                  warning={isInvalidMultiple}
                />
              </div>
            </div>

            {/* Fulfillment (AC3) — Collection + Delivery cards */}
            <div className="space-y-2">
              {/* Collection */}
              <div
                className={clsx(
                  'rounded-xl border overflow-hidden transition-colors',
                  !selectedBranchOption?.canCollect
                    ? 'border-gray-100'
                    : method === 'pickup'
                      ? 'border-brand-primary'
                      : 'border-gray-200 hover:border-gray-300',
                )}
              >
                <button
                  type="button"
                  onClick={() => selectedBranchOption?.canCollect && setMethod('pickup')}
                  disabled={!selectedBranchOption?.canCollect}
                  className="w-full px-4 py-2.5 text-left"
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={clsx(
                        'mt-0.5 h-4 w-4 flex-none rounded-full border-2',
                        method === 'pickup' && selectedBranchOption?.canCollect
                          ? 'border-brand-primary bg-brand-primary shadow-[inset_0_0_0_3px_white]'
                          : 'border-gray-300',
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={clsx(
                          'text-sm font-bold uppercase flex items-center gap-2',
                          selectedBranchOption?.canCollect ? 'text-gray-900' : 'text-gray-300',
                        )}
                      >
                        Collection
                      </p>
                      {isBranchStockPending ? (
                        <p className="text-xs text-gray-400 mt-0.5">Checking availability…</p>
                      ) : selectedBranchOption?.canCollect ? (
                        <p className="text-sm font-semibold text-gray-900 mt-0.5">
                          {collectionDateLabel ?? 'Date not available'}
                        </p>
                      ) : selectedBranchOption ? (
                        <p className="text-xs text-gray-400 mt-0.5">Not available for collection.</p>
                      ) : (
                        <p className="text-xs text-gray-400 mt-0.5">Not available for collection.</p>
                      )}
                    </div>
                  </div>
                </button>
                {/* Branch row + selector (AC4). Rendered whenever we have
                    a selected branch, even if it can't do pickup — the
                    engineer needs to see the current branch and be able
                    to swap to another one (matches ProductDetail). */}
                {selectedBranchOption && (
                  <>
                    <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-4 py-2">
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-800 min-w-0">
                        <MapPin className="h-3.5 w-3.5 flex-none text-gray-500" />
                        <span className="truncate">{selectedBranchOption.branchName}</span>
                        {selectedBranchOption.distanceMiles != null && (
                          <span className="text-gray-500 flex-shrink-0">
                            {' '}
                            · {selectedBranchOption.distanceMiles.toFixed(1)} mi
                          </span>
                        )}
                      </p>
                      {visibleBranchOptions.length > 1 && !showBranches && (
                        <button
                          type="button"
                          onClick={() => setShowBranches(true)}
                          className="text-xs font-semibold text-brand-primary hover:opacity-80 transition-opacity flex-shrink-0"
                        >
                          Change
                        </button>
                      )}
                    </div>
                    {showBranches && (
                      <div className="px-4 pb-3">
                        <div className="rounded-lg border border-gray-200 overflow-hidden">
                          {visibleBranchOptions.map((branch, idx) => {
                            const isSelected = branch.branchId === selectedBranchId
                            // Two-tier stock summary. Branch date is
                            // qty-independent — safe to show as-is. DC date
                            // is qty-aware for Wolseley/CPS: prefer the
                            // qty-matched value from freshDcDates when it's
                            // settled; while a recheck is in flight, fall
                            // back to the initial qty=1 date rather than
                            // flashing "Checking…" — small potential drift
                            // vs. clean UX. Only show "Later" as a last
                            // resort when there truly is no date to show.
                            const branchDateLabel = formatEarliestDate(
                              branch.earliestCollection,
                            )
                            const freshDc = freshDcDates.get(String(branch.branchId))
                            const freshDcDateLabel =
                              freshDc?.qty === quantity && !freshDc.isChecking
                                ? formatEarliestDate(freshDc.pickupDate)
                                : null
                            const dcDateLabel =
                              freshDcDateLabel ??
                              formatEarliestDate(branch.pickupDcDate) ??
                              'Later'
                            const summaryParts: string[] = []
                            // Only surface pickup availability when the branch
                            // can ACTUALLY do pickup (adapter's can_collect
                            // flag AND some pickup stock). Wolseley returns
                            // can_collect: false at branches that hold DC
                            // stock the depot can dispatch for DELIVERY but
                            // won't offer for pickup — reading pickup_dc_stock
                            // unconditionally showed "1,581 available" here
                            // while the top Pickup tile correctly said "Not
                            // available", confusing the engineer. Empty
                            // summaryParts falls through to "No stock" below
                            // which lines up with the tile.
                            if (branch.canCollect) {
                              if (branch.branchCollectionStock > 0) {
                                summaryParts.push(
                                  branchDateLabel
                                    ? `${branch.branchCollectionStock.toLocaleString()} available ${branchDateLabel}`
                                    : `${branch.branchCollectionStock.toLocaleString()} available · Collection date to be confirmed`,
                                )
                              }
                              // Hide the DC tier when its qty matches the branch
                              // tier — engineer would just see the same "N Date"
                              // twice (raw-pool suppliers where dc_stock equals
                              // branch_stock). Matches ProductDetail's rule at
                              // SupplierListingCard.tsx:338-345.
                              const dcMatchesBranch =
                                branch.pickupDcStock === branch.branchCollectionStock
                              if (branch.pickupDcStock > 0 && !dcMatchesBranch) {
                                summaryParts.push(
                                  `${branch.pickupDcStock.toLocaleString()} available ${dcDateLabel}`,
                                )
                              }
                            }
                            const hasAnyStock = summaryParts.length > 0
                            // Backorder fallback for the branch summary — if
                            // shelf + DC are both empty but the supplier
                            // allows backorder at this branch and quoted a
                            // date, show "Available on Xth Month" instead of
                            // the red "No stock". Consistent with the top
                            // Pickup tile, which already resolves to the
                            // backorder date via isPickupBackorder.
                            //
                            // For API-date backorder branches (Wolseley), the
                            // date is qty-aware — prefer the settled fresh
                            // value from the per-branch recheck so the row
                            // matches the Pickup tile at the current qty.
                            // Admin-date backorder is qty-invariant, so the
                            // stored value is fine.
                            const useFreshBackorderDate =
                              branch.backorderDateFromApi &&
                              freshDc?.qty === quantity &&
                              !freshDc.isChecking
                            const backorderDateForBranch = useFreshBackorderDate
                              ? (freshDc?.backorderPickupDate ?? null)
                              : branch.backorderCollectionDate
                            const backorderLabel =
                              !hasAnyStock &&
                              branch.canCollect &&
                              branch.backorderAllowed &&
                              backorderDateForBranch != null
                                ? formatBackorderAvailability(backorderDateForBranch)
                                : null
                            return (
                              <button
                                key={branch.branchId}
                                type="button"
                                onClick={() => {
                                  setSelectedBranchId(branch.branchId)
                                  setHasUserSelectedBranch(true)
                                  setShowBranches(false)
                                }}
                                className={clsx(
                                  'w-full px-3 py-2.5 text-left flex items-center justify-between transition-colors hover:bg-gray-50',
                                  idx > 0 && 'border-t border-gray-100',
                                  isSelected && 'bg-gray-50',
                                )}
                              >
                                <div className="min-w-0">
                                  <p className="text-sm text-gray-900">{branch.branchName}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {branch.distanceMiles != null
                                      ? `${branch.distanceMiles.toFixed(1)} mi away`
                                      : '—'}
                                    {' · '}
                                    {hasAnyStock ? (
                                      summaryParts.join(' · ')
                                    ) : backorderLabel ? (
                                      <span>{backorderLabel}</span>
                                    ) : (
                                      <span className="text-red-500">Out of stock</span>
                                    )}
                                  </p>
                                </div>
                                {isSelected && <Check className="w-4 h-4 text-brand-primary" />}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Delivery */}
              <button
                type="button"
                onClick={() => selectedBranchOption?.canDeliver && setMethod('delivery')}
                disabled={!selectedBranchOption?.canDeliver}
                className={clsx(
                  'w-full rounded-xl border px-4 py-3 text-left transition-colors',
                  !selectedBranchOption?.canDeliver
                    ? 'border-gray-100 cursor-not-allowed'
                    : method === 'delivery'
                      ? 'border-brand-primary'
                      : 'border-gray-200 hover:border-gray-300',
                )}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={clsx(
                      'mt-0.5 h-4 w-4 flex-none rounded-full border-2',
                      method === 'delivery' && selectedBranchOption?.canDeliver
                        ? 'border-brand-primary bg-brand-primary shadow-[inset_0_0_0_3px_white]'
                        : 'border-gray-300',
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={clsx(
                        'text-sm font-bold uppercase flex items-center gap-2',
                        selectedBranchOption?.canDeliver ? 'text-gray-900' : 'text-gray-300',
                      )}
                    >
                      Delivery
                    </p>
                    {isBranchStockPending ? (
                      <p className="text-xs text-gray-400 mt-0.5">Checking availability…</p>
                    ) : selectedBranchOption?.canDeliver ? (
                      <>
                        <p className="text-sm font-semibold text-gray-900 mt-0.5">
                          {deliveryDateLabel ?? 'Date not available'}
                        </p>
                        {(() => {
                          // Prefer the fresh qty-matched source when the
                          // recheck has landed; else the initial qty=1
                          // source; else the picked branch. CPS's borrow
                          // pick is qty-dependent, so at high qty the
                          // real delivery branch may differ from qty=1.
                          const sourceName =
                            (selectedFreshReady
                              ? selectedFreshDc?.deliverySourceBranchName
                              : null) ??
                            selectedBranchOption.deliverySourceBranchName ??
                            selectedBranchOption.branchName
                          return sourceName ? (
                            <p className="text-xs text-gray-500 mt-0.5">
                              from {sourceName}
                            </p>
                          ) : null
                        })()}
                      </>
                    ) : selectedBranchOption ? (
                      <p className="text-xs text-gray-400 mt-0.5">Not available for delivery.</p>
                    ) : (
                      <p className="text-xs text-gray-400 mt-0.5">Not available for delivery.</p>
                    )}
                  </div>
                </div>
              </button>
            </div>

            {isLoading && (
              <p className="text-xs text-gray-500 px-1">Loading alternatives…</p>
            )}
          </div>

          {/* Footer (AC7) */}
          <div className="sticky bottom-0 px-4 py-3 border-t border-gray-100 bg-white">
            <Button
              onClick={handleApply}
              disabled={
                !hasChanges ||
                isApplying ||
                !selectedBranchOption ||
                !methodAvailableAtBranch ||
                isBranchStockPending ||
                isInvalidMultiple ||
                isOverStock ||
                quantity <= 0
              }
              variant="primary"
              className="w-full"
            >
              {isApplying
                ? 'Updating…'
                : isInvalidMultiple
                  ? `Sold in multiples of ${orderMultiple}`
                  : isOverStock && stockForMethod != null
                    ? `Only ${stockForMethod.toLocaleString()} available`
                    : 'Update this item'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
